import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ChevronDown, ChevronUp, Check, Clock, Layers, MapPin, X } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';

function Reveal({ children, delay = 0, className = '' }) {
  const { ref, isVisible } = useScrollReveal(0.08);
  return (
    <div
      ref={ref}
      className={`${isVisible ? 'reveal-visible' : 'reveal-hidden'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ─── Case Studies Hero ─── */
function CaseStudiesHero() {
  return (
    <section className="bg-brand-navy py-20 lg:py-28" data-testid="case-studies-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl">
          <Reveal>
            <p className="text-brand-orange font-semibold text-sm uppercase tracking-widest mb-4 font-source-sans">
              Client Results
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-lexend font-bold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-tight mb-6">
              Client <span className="text-brand-orange">Results</span>
            </h1>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-lexend font-semibold text-lg text-slate-300 mb-4">
              Proven Procurement Transformation Across Global Enterprises
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="text-slate-300 text-base leading-relaxed font-source-sans mb-2">
              30+ clients served across India, APAC, and Australia/NZ.
            </p>
            <p className="text-slate-400 text-sm leading-relaxed font-source-sans">
              All case studies are presented anonymously to protect client confidentiality.
              Specific metrics, ROI figures, and transformation models are available on request during consultation.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ─── Case Studies Data ─── */
const caseStudies = [
  {
    number: 1,
    industry: 'Amusement & Entertainment',
    geo: 'Singapore, Korea, UAE, India',
    title: 'Multi-Park Procurement Transformation',
    timeline: '8 Months',
    challenge: 'Fragmented sourcing processes, manual supplier onboarding, and limited spend visibility across multiple park operations in four countries.',
    solution: 'Implemented strategic procurement and Guided Buying across locations with centralized sourcing workflows, automated supplier onboarding, and unified spend dashboards.',
    results: [
      '60-70% faster sourcing cycle times',
      'Maverick spend significantly reduced',
      'Supplier onboarding fully automated',
      'Improved procurement compliance across business units',
    ],
    modules: 'Strategic Sourcing | Guided Buying',
    image: 'https://images.unsplash.com/photo-1627035983655-0ceec61bb733?crop=entropy&cs=srgb&fm=jpg&w=900&q=75',
    imageAlt: 'Amusement park transformation - multi-park procurement',
  },
  {
    number: 2,
    industry: 'FMCG / Entertainment / Tobacco',
    geo: 'Multi-region',
    title: 'Centralized Source-to-Pay Across Multi-Business Units',
    timeline: '5 Months',
    challenge: 'Decentralized procurement operations, price discrepancies, and contract leakage across multiple entities operating in different geographies.',
    solution: 'Unified sourcing and commerce automation to standardize procurement across business units, with centralized contract governance and spend visibility.',
    results: [
      '50-65% faster procurement cycles',
      'Full spend visibility achieved',
      'Contract leakage eliminated',
      'Standardized sourcing governance',
    ],
    modules: 'Sourcing Optimization | Commerce Automation',
    image: 'https://images.pexels.com/photos/5498026/pexels-photo-5498026.jpeg?auto=compress&cs=tinysrgb&h=500&w=900',
    imageAlt: 'Consumer goods warehouse and supply chain',
  },
  {
    number: 3,
    industry: 'Professional Services / IT Consulting',
    geo: 'India',
    title: 'Contract Digitization & Supplier Lifecycle Optimization',
    timeline: '4 Months',
    challenge: 'Service contracts were unmanaged and paper-based, supplier records were fragmented, and indirect procurement lacked any governance or visibility.',
    solution: 'Implemented Supplier Lifecycle & Performance management and Contract Management with automated approval workflows and supplier risk tracking.',
    results: [
      '100% contracts digitized',
      'Zero contract leakage',
      'Supplier lifecycle governance automated',
      'Improved vendor risk visibility',
    ],
    modules: 'Contract Management | Supplier Lifecycle',
    image: 'https://images.pexels.com/photos/7875785/pexels-photo-7875785.jpeg?auto=compress&cs=tinysrgb&h=500&w=900',
    imageAlt: 'Contract digitization and document management',
  },
  {
    number: 4,
    industry: 'Manufacturing',
    geo: 'Australia / New Zealand / Canada',
    title: 'Procurement Standardization Across Plants',
    timeline: '6 Months',
    challenge: 'Procurement processes differed significantly across manufacturing plants, creating costly delays, inconsistent supplier pricing, and poor PO compliance.',
    solution: 'Deployed Buying & Invoicing with Guided Buying and centralized catalog enablement across all plant locations.',
    results: [
      '45-60% reduction in procurement processing time',
      'Improved PO compliance across plants',
      'Faster invoice approvals',
      'Reduced manual intervention',
    ],
    modules: 'Buying & Invoicing | Guided Buying',
    image: 'https://images.pexels.com/photos/34718930/pexels-photo-34718930.jpeg?auto=compress&cs=tinysrgb&h=500&w=900',
    imageAlt: 'Manufacturing factory floor and production',
  },
  {
    number: 5,
    industry: 'Energy & Utilities',
    geo: 'Middle East',
    title: 'Supplier Risk & Compliance Transformation',
    timeline: '7 Months',
    challenge: 'High-risk supplier environment, fragmented qualification processes across business units, and significant compliance gaps creating audit exposure.',
    solution: 'Deployed Supplier Lifecycle & Performance management with supplier qualification workflows, risk scoring, and compliance tracking.',
    results: [
      '100% supplier qualification digitized',
      'Reduced supplier onboarding delays',
      'Stronger compliance governance',
      'Improved audit readiness',
    ],
    modules: 'Supplier Lifecycle | Supplier Risk Management',
    image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?crop=entropy&cs=srgb&fm=jpg&w=900&q=75',
    imageAlt: 'Energy utilities infrastructure transformation',
  },
  {
    number: 6,
    industry: 'Retail & Consumer Goods',
    geo: 'APAC',
    title: 'Catalog & Guided Buying Optimization',
    timeline: '3 Months',
    challenge: 'Large indirect spend leakage due to off-contract purchases and poor guided buying adoption across a large employee base.',
    solution: 'Optimized Guided Buying configuration and supplier catalog strategy with improved search, category guidance, and purchasing workflows.',
    results: [
      '70% increase in guided buying adoption',
      'Reduced off-contract purchases',
      'Improved spend control',
      'Better user buying experience',
    ],
    modules: 'Guided Buying | Catalog Enablement',
    image: 'https://images.unsplash.com/photo-1578916171728-46686eac8d58?crop=entropy&cs=srgb&fm=jpg&w=900&q=75',
    imageAlt: 'Retail store and consumer goods supply chain',
  },
  {
    number: 7,
    industry: 'Healthcare & Pharmaceuticals',
    geo: 'India & UAE',
    title: 'Invoice Automation & AP Efficiency',
    timeline: '5 Months',
    challenge: 'Manual invoice handling across multiple vendors, delayed approval cycles, and poor invoice visibility creating cash flow and compliance issues.',
    solution: 'Implemented Buying & Invoicing integrated with S/4HANA for end-to-end invoice automation, touchless processing, and real-time AP visibility.',
    results: [
      '65-80% reduction in invoice processing time',
      'Higher touchless invoice automation',
      'Reduced payment delays',
      'Improved AP productivity',
    ],
    modules: 'Buying & Invoicing | ERP Integration',
    image: 'https://images.pexels.com/photos/8539078/pexels-photo-8539078.jpeg?auto=compress&cs=tinysrgb&h=500&w=900',
    imageAlt: 'Healthcare pharmaceutical supply chain and automation',
  },
  {
    number: 8,
    industry: 'Automotive',
    geo: 'Southeast Asia',
    title: 'Regional Procurement Consolidation',
    timeline: '9 Months',
    challenge: 'Multiple procurement systems across regional entities causing inconsistent sourcing, fragmented reporting, and supplier duplication.',
    solution: 'Sourcing, Contracts, and centralized supplier collaboration rollout across all regional entities with standardized governance model.',
    results: [
      'Unified procurement model across countries',
      'Better spend analytics visibility',
      'Faster supplier collaboration',
      'Reduced sourcing duplication',
    ],
    modules: 'Strategic Sourcing | Contracts | Supplier Collaboration',
    image: 'https://images.unsplash.com/photo-1567789884554-0b844b597180?crop=entropy&cs=srgb&fm=jpg&w=900&q=75',
    imageAlt: 'Automotive manufacturing and regional procurement',
  },
  {
    number: 9,
    industry: 'Telecom',
    geo: 'India & APAC',
    title: 'AMS & Continuous Optimization',
    timeline: 'Ongoing AMS Support',
    challenge: 'Frequent support issues, low user adoption, and performance bottlenecks following a prior implementation by a different partner.',
    solution: 'Delivered full Managed Services (AMS) with L2/L3 support, release management, process optimization, and ongoing user adoption programs.',
    results: [
      'Improved system uptime & stability',
      'Faster issue resolution',
      'Increased user adoption',
      'Reduced operational disruption',
    ],
    modules: 'Managed Services (AMS) | Buying & Invoicing | Sourcing',
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&w=900&q=75',
    imageAlt: 'Telecom network and managed services',
  },
];

/* ─── Case Study Card ─── */
function CaseStudyCard({ cs, isExpanded, onToggle }) {
  return (
    <Reveal>
      <article
        className="bg-white rounded-2xl border border-brand-border overflow-hidden transition-all duration-300"
        data-testid={`case-study-${cs.number}`}
      >
        {/* Card Header — always visible */}
        <div className="px-6 sm:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="inline-block px-3 py-1 bg-brand-orange text-white text-xs font-semibold rounded-full font-source-sans">
                  {cs.industry}
                </span>
                <span className="flex items-center gap-1 text-xs text-slate-500 font-source-sans">
                  <MapPin size={11} className="text-slate-400" />
                  {cs.geo}
                </span>
              </div>
              <h2 className="font-lexend font-bold text-lg sm:text-xl text-brand-navy leading-snug">{cs.title}</h2>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="flex items-center gap-1.5 text-slate-500 text-sm font-source-sans">
                <Clock size={13} className="text-brand-orange" />
                {cs.timeline}
              </span>
            </div>
          </div>

          {/* Modules — always visible */}
          <div className="flex items-center gap-2 mt-3">
            <Layers size={13} className="text-brand-orange flex-shrink-0" />
            <span className="text-xs text-slate-500 font-source-sans">{cs.modules}</span>
          </div>

          {/* Toggle button */}
          <button
            onClick={onToggle}
            className="mt-5 flex items-center gap-2 text-sm font-semibold text-brand-orange hover:text-brand-orange-dark transition-colors duration-150 font-source-sans group"
            data-testid={`case-study-${cs.number}-toggle`}
          >
            {isExpanded ? (
              <>
                <ChevronUp size={16} />
                Close Details
              </>
            ) : (
              <>
                View Case Study
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-150" />
              </>
            )}
          </button>
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div className="border-t border-brand-border">
            {/* Industry image */}
            <div className="w-full h-52 sm:h-64 overflow-hidden">
              <img
                src={cs.image}
                alt={cs.imageAlt}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Challenge / Solution / Results */}
            <div className="px-6 sm:px-8 py-8 grid grid-cols-1 md:grid-cols-3 gap-8 bg-brand-bg">
              <div>
                <h4 className="font-lexend font-semibold text-xs uppercase tracking-wider text-slate-500 mb-3">
                  The Challenge
                </h4>
                <p className="text-slate-700 text-sm leading-relaxed font-source-sans">{cs.challenge}</p>
              </div>
              <div>
                <h4 className="font-lexend font-semibold text-xs uppercase tracking-wider text-slate-500 mb-3">
                  Our Solution
                </h4>
                <p className="text-slate-700 text-sm leading-relaxed font-source-sans">{cs.solution}</p>
              </div>
              <div>
                <h4 className="font-lexend font-semibold text-xs uppercase tracking-wider text-slate-500 mb-3">
                  Results Delivered
                </h4>
                <ul className="space-y-2">
                  {cs.results.map((r) => (
                    <li key={r} className="flex items-start gap-2 text-sm font-source-sans">
                      <Check size={14} className="text-brand-orange mt-0.5 flex-shrink-0" />
                      <span className="font-semibold text-slate-800">{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Close button */}
            <div className="px-8 py-4 bg-white border-t border-brand-border flex justify-end">
              <button
                onClick={onToggle}
                className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700 font-source-sans font-medium transition-colors"
                data-testid={`case-study-${cs.number}-close`}
              >
                <X size={13} />
                Close
              </button>
            </div>
          </div>
        )}
      </article>
    </Reveal>
  );
}

/* ─── Why Clients Engage With Us ─── */
function WhyClientsEngage() {
  const points = [
    '30+ engagements across APAC, India & ANZ',
    'Expertise across full Source-to-Pay lifecycle',
    'Deep integration capability: ERP, Business Networks, cloud platforms',
    'Enterprise rollout, optimization & managed services support',
    'Outcome-focused procurement transformation',
  ];

  return (
    <section className="py-16 bg-brand-orange border-t border-brand-navy/10" data-testid="why-clients-engage">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="font-lexend font-bold text-2xl lg:text-3xl text-brand-navy mb-8">
              Why Clients Engage With Us
            </h3>
            <ul className="space-y-3 text-left mb-8">
              {points.map((p) => (
                <li key={p} className="flex items-start gap-3 bg-white rounded-lg px-5 py-4 border border-brand-navy/10">
                  <Check size={16} className="text-brand-orange mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700 text-sm font-source-sans font-medium">{p}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-col items-center">
              <a
                href="https://wa.me/919637625311"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-orange"
                data-testid="case-studies-cta-btn"
              >
                Talk to us <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function CaseStudies() {
  const [expandedId, setExpandedId] = useState(null);

  const handleToggle = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div data-testid="case-studies-page">
      <Helmet>
        <title>Client Success Stories | iMANE Procurement Case Studies | Mane Labs LLP</title>
        <meta name="description" content="Explore iMANE's client success stories across FMCG, Manufacturing, Healthcare, Retail, Energy and more. 50-70% cost reductions. SAP Ariba implementations across India, APAC, ANZ, and Middle East." />
        <meta name="keywords" content="procurement case studies, SAP Ariba case studies, iMANE case studies, mane labs case studies, supply chain transformation results, procurement ROI India" />
        <link rel="canonical" href="https://www.imane.in/case-studies" />
        <meta property="og:title" content="Client Success Stories | iMANE Procurement Case Studies" />
        <meta property="og:description" content="50-70% cost reductions. SAP Ariba implementations. Real results across FMCG, Manufacturing, Healthcare, Retail, and more." />
        <meta property="og:url" content="https://www.imane.in/case-studies" />
      </Helmet>
      <CaseStudiesHero />

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
          {caseStudies.map((cs) => (
            <CaseStudyCard
              key={cs.number}
              cs={cs}
              isExpanded={expandedId === cs.number}
              onToggle={() => handleToggle(cs.number)}
            />
          ))}
        </div>
      </section>

      <WhyClientsEngage />
    </div>
  );
}
